import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { motion } from "framer-motion";
import { updateBlog, fetchBlogs } from "../../../redux/actions/blogActions";
import {
  FiX,
  FiRefreshCw,
  FiEdit,
  FiType,
  FiGlobe,
  FiUser,
  FiImage,
  FiFileText,
  FiCheckCircle,
  FiTag,
  FiSearch,
  FiCheck,
} from "react-icons/fi";

const EditBlog = ({ blog, close }: any) => {
  const dispatch = useDispatch<any>();

  // State for all dynamic fields
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [shortDescription, setShortDescription] = useState("");
  const [content, setContent] = useState("");
  const [author, setAuthor] = useState("");
  const [tags, setTags] = useState("");
  const [metaTitle, setMetaTitle] = useState("");
  const [status, setStatus] = useState("");
  const [featuredImage, setFeaturedImage] = useState<File | null>(null);

  useEffect(() => {
    if (blog) {
      setTitle(blog.title || "");
      setSlug(blog.slug || "");
      setShortDescription(blog.short_description || "");
      setContent(blog.content || "");
      setAuthor(blog.author || "");
      setTags(blog.tags || "");
      setMetaTitle(blog.meta_title || "");
      setStatus(blog.status || "published");
    }
  }, [blog]);

  const handleUpdate = async (e: any) => {
    e.preventDefault();

    const data = new FormData();
    data.append("title", title);
    data.append("slug", slug);
    data.append("short_description", shortDescription);
    data.append("content", content);
    data.append("author", author);
    data.append("tags", tags);
    data.append("meta_title", metaTitle);
    data.append("status", status);

    if (featuredImage) {
      data.append("featured_image", featuredImage);
    }

    await dispatch(updateBlog(blog.id, data));
    dispatch(fetchBlogs());
    close();
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-6 sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 text-white">
              <div className="p-2 bg-white/20 rounded-lg">
                <FiEdit className="text-xl" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Edit Blog Post</h2>
                <p className="text-emerald-100 text-sm">
                  Updating details for: {blog.title}
                </p>
              </div>
            </div>
            <button
              onClick={close}
              className="text-white/80 hover:text-white transition-colors"
            >
              <FiX size={24} />
            </button>
          </div>
        </div>

        <form onSubmit={handleUpdate} className="p-8 space-y-6">
          {/* Row 1: Title & Slug */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-sm font-semibold dark:text-gray-300 flex items-center gap-2">
                <FiType /> Title
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full bg-gray-50 dark:bg-gray-700 border dark:border-gray-600 rounded-xl py-3 px-4 dark:text-white outline-none focus:ring-2 focus:ring-emerald-500"
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold dark:text-gray-300 flex items-center gap-2">
                <FiGlobe /> Slug
              </label>
              <input
                type="text"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                className="w-full bg-gray-50 dark:bg-gray-700 border dark:border-gray-600 rounded-xl py-3 px-4 dark:text-white outline-none focus:ring-2 focus:ring-emerald-500"
                required
              />
            </div>
          </div>

          {/* Row 2: Short Description */}
          <div className="space-y-2">
            <label className="text-sm font-semibold dark:text-gray-300 flex items-center gap-2">
              <FiFileText /> Short Description
            </label>
            <textarea
              rows={2}
              value={shortDescription}
              onChange={(e) => setShortDescription(e.target.value)}
              className="w-full bg-gray-50 dark:bg-gray-700 border dark:border-gray-600 rounded-xl py-3 px-4 dark:text-white outline-none focus:ring-2 focus:ring-emerald-500"
              placeholder="Brief summary for the blog card..."
            />
          </div>

          {/* Row 3: Main Content */}
          <div className="space-y-2">
            <label className="text-sm font-semibold dark:text-gray-300 flex items-center gap-2">
              <FiFileText /> Content (HTML)
            </label>
            <textarea
              rows={10}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="w-full bg-gray-50 dark:bg-gray-700 border dark:border-gray-600 rounded-xl py-3 px-4 dark:text-white font-mono text-sm outline-none focus:ring-2 focus:ring-emerald-500"
              required
            />
          </div>

          {/* Row 4: Author, Tags, Status */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <label className="text-sm font-semibold dark:text-gray-300 flex items-center gap-2">
                <FiUser /> Author
              </label>
              <input
                type="text"
                value={author}
                onChange={(e) => setAuthor(e.target.value)}
                className="w-full bg-gray-50 dark:bg-gray-700 border dark:border-gray-600 rounded-xl py-3 px-4 dark:text-white outline-none"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold dark:text-gray-300 flex items-center gap-2">
                <FiTag /> Tags
              </label>
              <input
                type="text"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                className="w-full bg-gray-50 dark:bg-gray-700 border dark:border-gray-600 rounded-xl py-3 px-4 dark:text-white outline-none"
                placeholder="web, tech, ai"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold dark:text-gray-300 flex items-center gap-2">
                <FiCheckCircle /> Status
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full bg-gray-50 dark:bg-gray-700 border dark:border-gray-600 rounded-xl py-3 px-4 dark:text-white outline-none"
              >
                <option value="published">Published</option>
                <option value="draft">Draft</option>
              </select>
            </div>
          </div>

          {/* Row 5: SEO & Image */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-sm font-semibold dark:text-gray-300 flex items-center gap-2">
                <FiSearch /> Meta Title
              </label>
              <input
                type="text"
                value={metaTitle}
                onChange={(e) => setMetaTitle(e.target.value)}
                className="w-full bg-gray-50 dark:bg-gray-700 border dark:border-gray-600 rounded-xl py-3 px-4 dark:text-white outline-none"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                <FiImage className="text-emerald-500" /> Featured Image
              </label>
              <input
                type="file"
                onChange={(e: any) => setFeaturedImage(e.target.files[0])}
                className="block w-full text-sm text-gray-500 dark:text-gray-400
      file:mr-4 file:py-2.5 file:px-6
      file:rounded-full file:border-0
      file:text-sm file:font-bold
      file:bg-emerald-50 file:text-emerald-700
      dark:file:bg-emerald-900/30 dark:file:text-emerald-400
      file:cursor-pointer file:transition-all file:duration-300
      hover:file:bg-emerald-100 dark:hover:file:bg-emerald-900/50
      hover:file:scale-105
      active:file:scale-95"
              />
              {/* Optional: Show file name if exists */}
              {featuredImage && (
                <p className="text-[10px] text-emerald-600 dark:text-emerald-400 font-medium italic mt-1 ml-2">
                  Ready to upload: {featuredImage.name}
                </p>
              )}
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={close}
              className="flex-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 py-3 rounded-xl font-semibold hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-200"
            >
              Discard Changes
            </button>

            <motion.button
              type="submit"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              whileHover={{
                scale: 1.02,
                boxShadow: "0 10px 15px -3px rgba(16, 185, 129, 0.3)",
              }}
              whileTap={{ scale: 0.95 }}
              className="flex-1 bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-3 rounded-xl font-bold shadow-lg shadow-emerald-500/20 flex items-center justify-center gap-2 transition-all cursor-pointer"
            >
              <FiRefreshCw className="text-lg" />
              Update Post
            </motion.button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditBlog;
